/**
 * Mail address prefix validation utilities.
 *
 * Contains format validation + CS/EN profanity blacklist to prevent
 * offensive or inappropriate mailbox prefixes.
 */

/** Minimum prefix length (must match backend regex) */
const MIN_PREFIX_LENGTH = 3;

/**
 * Maximum prefix length (must match backend regex). 119 = the 128-char column minus the `-`
 * and the 8-char random suffix the server appends.
 */
const MAX_PREFIX_LENGTH = 119;

/**
 * Backend-compatible regex for prefix format:
 * - lowercase alphanumeric + hyphens
 * - cannot start or end with a hyphen
 * - 3-119 characters
 */
const PREFIX_REGEX = /^[a-z0-9][a-z0-9-]{1,117}[a-z0-9]$/;

/**
 * Blacklisted prefix patterns — CS/EN profanity and offensive words.
 * Checked as substrings against the normalized (lowercase) prefix.
 */
const BLACKLISTED_PATTERNS: readonly string[] = [
  // English profanity
  'fuck',
  'shit',
  'asshole',
  'bitch',
  'dick',
  'pussy',
  'bastard',
  'damn',
  'cunt',
  'nigger',
  'nigga',
  'faggot',
  'retard',
  'whore',
  'slut',
  'cock',
  'penis',
  'vagina',
  'porn',
  'nazi',
  'hitler',
  'rape',
  'molest',
  'pedo',
  'kill',
  'murder',
  'terrorist',
  'bomb',
  // Czech profanity
  'kurva',
  'prdel',
  'hovno',
  'srac',
  'debil',
  'kokot',
  'picat',
  'picka',
  'pica',
  'zmrd',
  'vole',
  'kretén',
  'kreten',
  'idiot',
  'hajzl',
  'buzna',
  'cigan',
  'negr',
  'kundat',
  'kunda',
  'mamrd',
  'sviné',
  'svine',
  'blbec',
  'pitomec',
  'magor',
  'vycur',
  // Spam-like
  'admin',
  'postmaster',
  'abuse',
  'noreply',
  'no-reply',
  'mailer-daemon',
  'root',
  'webmaster',
  'hostmaster',
  'info',
  'support',
  'test',
  'spam'
] as const;

type PrefixValidationError = 'required' | 'tooShort' | 'tooLong' | 'invalidFormat' | 'blacklisted';

/**
 * Validate a mail address prefix.
 * Returns `null` if valid, or an error key for translation.
 */
export function validateMailPrefix(prefix: string): PrefixValidationError | null {
  if (!prefix || prefix.trim().length === 0) {
    return 'required';
  }

  const normalized = prefix.toLowerCase().trim();

  if (normalized.length < MIN_PREFIX_LENGTH) {
    return 'tooShort';
  }

  if (normalized.length > MAX_PREFIX_LENGTH) {
    return 'tooLong';
  }

  if (!PREFIX_REGEX.test(normalized)) {
    return 'invalidFormat';
  }

  // Check blacklist — substring match
  const isBlacklisted = BLACKLISTED_PATTERNS.some(pattern => normalized.includes(pattern));
  if (isBlacklisted) {
    return 'blacklisted';
  }

  return null;
}

export { MIN_PREFIX_LENGTH, MAX_PREFIX_LENGTH };
export type { PrefixValidationError };
